import { noop, valueProvider } from '@proc7ts/primitives';
import { alwaysSupply, Supply } from '@proc7ts/supply';

/**
 * An error indicating the work is already done.
 */
class WorkDoneError extends TypeError {
    /**
     * Constructs a work done error.
     *
     * @param workload - A workload the work is done for.
     * @param work - The work already done, if any.
     * @param reason - The reason of work failure, or `undefined` if the work completed successfully.
     * @param message - Custom error message.
     */
    constructor(workload, work, reason, message = workload.workName(work) + (reason !== undefined
        ? ` already terminated (${reason})`
        : ' already done')) {
        super(message);
        this.workload = workload;
        this.work = work;
        this.reason = reason;
    }
}

/**
 * A workload able to perform a work within a workbench.
 *
 * The work specific to workload is represented by work instances. Such instances are available in {@link Workbench.work
 * workbench} and can be used e.g. to run workload-specific tasks.
 *
 * The same workload instance can be used in different workbenches.
 *
 * @typeParam TWork - A work type performed by this workload.
 */
class Workload {
    /**
     * Constructs a workload.
     *
     * @param name - Human-readable workload name.
     * @param allocator - A work allocator specific to constructed workload.
     */
    constructor(name, allocator) {
        this.name = name;
        this.allocator = allocator;
    }
    /**
     * Builds a human-readable name of the work performed by this workload.
     *
     * This is used e.g. to construct a default error message for {@link WorkDoneError}.
     *
     * @param _work - Target work.
     *
     * @returns A string containing work name.
     */
    workName(_work) {
        return `The work of ${this.name}`;
    }
    toString() {
        return `Workload(${this.name})`;
    }
}

/**
 * @internal
 */
class OrderedTasks {
    constructor(_allotment) {
        this._allotment = _allotment;
        this._queues = new Map();
    }
    runAfter(workload, task) {
        let queue = this._queues.get(workload);
        if (!queue) {
            queue = new TaskQueue(this._allotment);
            this._queues.set(workload, queue);
        }
        return queue.enqueue(task);
    }
}
class TaskQueue {
    constructor(_allotment) {
        this._allotment = _allotment;
        /**
         * Enqueued tasks.
         *
         * The subsequent tasks are waiting for preceding ones. The very first one is always running.
         */
        this._tasks = [];
    }
    enqueue(task) {
        return new Promise(resolve => {
            const entry = {
                task,
                run: () => {
                    resolve(this._allotment.run(task).finally(() => {
                        // Remove from the queue.
                        this._tasks.shift();
                        // Run next.
                        this._runNext();
                    }));
                },
            };
            this._tasks.push(entry);
            if (this._tasks.length === 1) {
                // The first task is just enqueued.
                // Run it immediately, as there is nothing to wait for.
                this._runNext();
            }
        });
    }
    _runNext() {
        const [first] = this._tasks;
        if (first) {
            first.run();
        }
    }
}

/**
 * @internal
 */
class WorkOrdering extends Workload {
    constructor() {
        super('ordering', {
            start(allotment) {
                return new OrderedTasks(allotment);
            },
        });
    }
}
WorkOrdering.$ = new WorkOrdering();

class WorkStage extends Workload {
    constructor(name, allocator = {}) {
        super(name, {
            start(allotment) {
                const { workbench, workload, supply } = allotment;
                const runner = new WorkStageRunner(allotment, allocator);
                return {
                    workbench,
                    stage: workload,
                    supply,
                    async run(task) {
                        return await runner.run(this, task);
                    },
                };
            },
        });
    }
    workName(_work) {
        return `The ${this.name} stage`;
    }
    toString() {
        return `WorkStage(${this.name})`;
    }
}
class WorkStageRunner {
    constructor(allotment, allocator) {
        this.allotment = allotment;
        this.allocator = allocator;
        this._whenTaskDone = Promise.resolve();
        const { supply } = allotment;
        this._whenAllDone = new Promise(resolve => this._end = resolve)
            .then(() => supply.off())
            .catch(error => supply.off(error));
        supply.whenOff(reason => {
            if (reason === undefined) {
                this._end();
            }
            else {
                this._end(Promise.reject(reason));
            }
            // Stop accepting new tasks.
            this.run = (work, _task) => Promise.reject(new WorkDoneError(allotment.workload, work, reason));
        });
    }
    run(work, task) {
        const promise = this._start(work).then(() => this.allotment.run(task));
        this._addTask(promise);
        return promise;
    }
    _addTask(taskPromise) {
        const taskDone = this._whenTaskDone = Promise.all([
            this._whenTaskDone,
            taskPromise.catch(noop),
        ]);
        taskDone.finally(() => {
            if (taskDone === this._whenTaskDone) {
                this._end(taskDone);
            }
        });
    }
    _start(work) {
        let whenStarted = this._awaitDeps();
        if (this.allocator.start) {
            whenStarted = whenStarted.then(async () => {
                await this.allocator.start(work);
            });
        }
        // Start only once!
        this._start = valueProvider(whenStarted);
        this._addTask(whenStarted);
        return whenStarted;
    }
    _awaitDeps() {
        const deps = [];
        const addDep = (dep) => {
            deps.push(new Promise(resolve => {
                this.allotment
                    .workbench
                    .work(WorkOrdering.$)
                    .runAfter(dep, () => {
                    resolve();
                    return this._whenAllDone;
                })
                    .catch(noop);
            }));
        };
        const { after } = this.allocator;
        if (after) {
            addDep(after);
        }
        addDep(this.allotment.workload);
        return Promise.all(deps);
    }
}

/**
 * @internal
 */
const Workbench$impl__symbol = ( /*#__PURE__*/Symbol('Workbench.impl'));
/**
 * @internal
 */
class Workbench$ {
    constructor(workbench, options) {
        this.workbench = workbench;
        this._works = new Map();
        const { supply = alwaysSupply(), run } = options;
        this.supply = new Supply(reason => {
            // Reject new work.
            this.work = workload => {
                throw new WorkDoneError(workload, undefined, reason, 'The workbench is stopped');
            };
        }).as(supply);
        this._run = run ? run.bind(options) : Workbench$run;
    }
    work(workload) {
        if (this._works.has(workload)) {
            return this._works.get(workload);
        }
        let ensureWorking = noop;
        const runWorkTask = async (work, task) => {
            const result = await this._run(task, work, workload);
            ensureWorking();
            return result;
        };
        const supply = new Supply().needs(this.supply);
        let setWork;
        let work;
        let runTask;
        const whenWork = new Promise(resolve => {
            setWork = newWork => {
                work = newWork;
                this._works.set(workload, newWork);
                runTask = async (task) => await runWorkTask(newWork, task);
                resolve(newWork);
                return newWork;
            };
        });
        runTask = async (task) => await runWorkTask(await whenWork, task);
        supply.whenOff(reason => {
            runTask = ensureWorking = () => {
                throw new WorkDoneError(workload, work, reason);
            };
            this._works.delete(workload);
        });
        return setWork(workload.allocator.start({
            workbench: this.workbench,
            workload,
            supply,
            async run(task) {
                return await runTask(task);
            },
        }));
    }
}
async function Workbench$run(task, _work, _workload) {
    return await task();
}

/**
 * A workbench for coordinated {@link Workload workloads}.
 *
 * Performs a work by running tasks specific to each kind of workload.
 */
class Workbench {
    /**
     * Constructs a workbench.
     *
     * @param options - Constructed workbench options.
     */
    constructor(options = {}) {
        this[Workbench$impl__symbol] = new Workbench$(this, options);
    }
    /**
     * Workbench supply.
     *
     * Once cut off the workbench terminates all current works, and no longer accepts new ones.
     */
    get supply() {
        return this[Workbench$impl__symbol].supply;
    }
    /**
     * Obtains a work of the given workload.
     *
     * Caches the work previously obtained work of the same workload, until the work is {@link Workload.Allotment.supply
     * disposed}. Creates a new work instance after that.
     *
     * @typeParam TWork - A work type.
     * @param workload - Target workload.
     *
     * @returns A work instance.
     */
    work(workload) {
        return this[Workbench$impl__symbol].work(workload);
    }
}

export { WorkDoneError, WorkStage, Workbench, Workload };
//# sourceMappingURL=workbench.js.map
